/**
 * Persisted project state (SPEC §11 "Intent"): what the operator wants, stored by the
 * graphics app in lowdb (`data/project.json`, human-readable, hand-editable). Observed
 * state (what OBS/CoreVideo/Sheets report) is never stored here.
 *
 * The schema is versioned. {@link migrateProject} upgrades older files step by step and
 * refuses files from a newer app version or without a version, loudly, instead of
 * resetting a show's state.
 */
import { z } from 'zod';
import { DensitySchema } from './bundle.ts';
import { CameraIdSchema, CameraSceneKeySchema, CAMERA_IDS, CAMERA_SCENE_KEYS, SceneKeySchema, SHARE_ID, type CameraSceneKey } from './fixture.ts';
import { ObsSceneItemBindingSchema } from './obs.ts';
import { PaletteColorsSchema } from './palette.ts';
import { PendingChangeSchema } from './protocol.ts';
import { ParticipantSchema, SessionSchema } from './session.ts';
import { parseOrThrow, ValidationError } from './validate.ts';

/**
 * - 1: Phase 2.
 * - 2: palette overrides per bundle and the density level (ADR 0002).
 * - 3: `coreVideoJoinName`, the name CoreVideo last joined with from the admin.
 */
export const PROJECT_SCHEMA_VERSION = 3;

const id = z.string().trim().min(1).max(128);
const timestamp = z.iso.datetime({ offset: true });

/** A session with its normalized participants, as last fetched from the sheet. */
export const SheetSessionSchema = z.strictObject({
  session: SessionSchema,
  /** Host first, then by `order`. */
  participants: z.array(ParticipantSchema).max(6),
});
export type SheetSession = z.infer<typeof SheetSessionSchema>;

/** The last-known-good sheet snapshot (SPEC §6: kept when the sheet becomes unavailable). */
export const SheetSnapshotSchema = z.strictObject({
  fetchedAt: timestamp,
  /** Every session in sheet order, cancelled ones included (the UI hides them). */
  sessions: z.array(SheetSessionSchema).max(500),
});
export type SheetSnapshot = z.infer<typeof SheetSnapshotSchema>;

/**
 * One generated participant ID (SPEC §6: IDs come from the graphics app, never the sheet).
 * Matched on refresh by (session, normalized name), then by (session, speaker order).
 */
export const ParticipantIdEntrySchema = z.strictObject({
  participantId: id,
  sessionId: id,
  normalizedName: z.string().min(1).max(200),
  /** `h` for the host, the numeric order otherwise. */
  speakerOrder: z.union([z.literal('h'), z.number().int().min(0)]),
});
export type ParticipantIdEntry = z.infer<typeof ParticipantIdEntrySchema>;

/** What a camera input shows: participant (nameplate) and CoreVideo feed, set together. */
export const CameraIntentSchema = z.strictObject({
  cameraId: CameraIdSchema,
  participantId: id.nullable(),
  coreVideoParticipantId: z.number().int().min(0).nullable(),
});
export type CameraIntent = z.infer<typeof CameraIntentSchema>;

/** What a binding points at inside its scene. */
export const BindingTargetSchema = z.enum([...CAMERA_IDS, SHARE_ID, 'gfxBackground', 'gfxCameras', 'gfxOverlay', 'gfxScene']);
export type BindingTarget = z.infer<typeof BindingTargetSchema>;

/** A claimed scene item (SPEC §8.3). Broken bindings stay broken until Bind fixture runs again. */
export const StoredBindingSchema = z.strictObject({
  sceneKey: SceneKeySchema,
  target: BindingTargetSchema,
  binding: ObsSceneItemBindingSchema,
  broken: z.boolean(),
  /** Why it broke, e.g. "scene item removed in OBS". */
  brokenReason: z.string().max(500).nullable(),
});
export type StoredBinding = z.infer<typeof StoredBindingSchema>;

/** The deferred action behind a Pending change (SPEC §5.4 rule D). */
export const PendingActionSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('assignment'),
    cameraId: CameraIdSchema,
    participantId: id,
    coreVideoParticipantId: z.number().int().min(0),
  }),
  z.strictObject({ kind: z.literal('layout'), sceneKey: CameraSceneKeySchema }),
]);
export type PendingAction = z.infer<typeof PendingActionSchema>;

export const PendingEntrySchema = z.strictObject({
  change: PendingChangeSchema,
  action: PendingActionSchema,
});
export type PendingEntry = z.infer<typeof PendingEntrySchema>;

/** A logged decision to proceed despite a preflight warning (SPEC §12). */
export const PreflightOverrideSchema = z.strictObject({
  checkId: z.string().min(1).max(100),
  sessionId: id.nullable(),
  at: timestamp,
  note: z.string().max(500).nullable(),
});
export type PreflightOverride = z.infer<typeof PreflightOverrideSchema>;

export const ProjectStateSchema = z.strictObject({
  schemaVersion: z.literal(PROJECT_SCHEMA_VERSION),
  /** Increments on every intent change (SPEC §11). */
  intentRevision: z.number().int().min(0),
  sheet: SheetSnapshotSchema.nullable(),
  participantIds: z.array(ParticipantIdEntrySchema).max(5000),
  /** Learned Zoom display names per participant (SPEC §6). */
  aliases: z.record(id, z.array(z.string().max(200)).max(50)),
  currentSessionId: id.nullable(),
  /** Exactly one entry per camera, CAM-1 … CAM-6. */
  cameras: z.array(CameraIntentSchema).length(CAMERA_IDS.length),
  speakerShare: z.strictObject({ cameraId: CameraIdSchema.nullable() }),
  bindings: z.strictObject({
    /** When Bind fixture last ran, and against which scene collection. */
    boundAt: timestamp.nullable(),
    sceneCollection: z.string().nullable(),
    items: z.array(StoredBindingSchema).max(200),
  }),
  /** Overlay toggles persist; the lower third is transient and is not stored. */
  toggles: z.strictObject({ sponsorBox: z.boolean(), logoBug: z.boolean(), timer: z.boolean() }),
  /** Bundle chosen in Prepare; `null` means the config's `bundle`. */
  bundle: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/).nullable(),
  /**
   * Palette overrides per bundle (role → colour), edited in Prepare (ADR 0002). Roles not
   * listed use the bundle default; roles the bundle no longer has are reported, not used.
   */
  paletteOverrides: z.record(z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/), PaletteColorsSchema),
  /** Density of the procedural environment (`signal` bundles). A show-time look control. */
  density: DensitySchema,
  /**
   * Layout file last applied to each camera scene (the GFX Cameras surfaces draw this, so
   * frames keep matching the video while a layout change waits in Pending).
   */
  appliedLayouts: z.strictObject(Object.fromEntries(CAMERA_SCENE_KEYS.map((k) => [k, z.string().nullable()])) as Record<CameraSceneKey, z.ZodNullable<z.ZodString>>),
  pending: z.array(PendingEntrySchema).max(100),
  preflightOverrides: z.array(PreflightOverrideSchema).max(500),
  /**
   * The display name of the last `joinMeeting` from the admin. CoreVideo appears in its own
   * roster under it, so the roster always ignores it (with `ignoredParticipantPatterns`).
   * Stored so it still applies after a restart while the bot stays in the meeting.
   */
  coreVideoJoinName: z.string().trim().min(1).max(100).nullable(),
});
export type ProjectState = z.infer<typeof ProjectStateSchema>;

/** A fresh project. */
export function initialProject(): ProjectState {
  return {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    intentRevision: 0,
    sheet: null,
    participantIds: [],
    aliases: {},
    currentSessionId: null,
    cameras: CAMERA_IDS.map((cameraId) => ({ cameraId, participantId: null, coreVideoParticipantId: null })),
    speakerShare: { cameraId: null },
    bindings: { boundAt: null, sceneCollection: null, items: [] },
    toggles: { sponsorBox: false, logoBug: false, timer: false },
    bundle: null,
    paletteOverrides: {},
    density: 'normal',
    appliedLayouts: Object.fromEntries(CAMERA_SCENE_KEYS.map((k) => [k, null])) as Record<CameraSceneKey, string | null>,
    pending: [],
    preflightOverrides: [],
    coreVideoJoinName: null,
  };
}

/** Upgrades raw data of version `from` to version `from + 1`. */
export type ProjectMigration = (raw: Record<string, unknown>) => Record<string, unknown>;

/** Migrations keyed by the version they upgrade *from*, each with a test. */
export const PROJECT_MIGRATIONS: Readonly<Record<number, ProjectMigration>> = {
  /** v2: no palette overrides yet, normal density. */
  1: (v1) => ({ ...v1, schemaVersion: 2, paletteOverrides: {}, density: 'normal' }),
  /** v3: no join name known yet. */
  2: (v2) => ({ ...v2, schemaVersion: 3, coreVideoJoinName: null }),
};

/**
 * Parses a stored project, upgrading it to {@link PROJECT_SCHEMA_VERSION}.
 * @param migrations Injected for tests; defaults to {@link PROJECT_MIGRATIONS}.
 * @throws ValidationError for a missing/unknown/newer version or an invalid result.
 */
export function migrateProject(
  raw: unknown,
  migrations: Readonly<Record<number, ProjectMigration>> = PROJECT_MIGRATIONS,
  currentVersion = PROJECT_SCHEMA_VERSION,
  schema: z.ZodType<ProjectState> = ProjectStateSchema,
): ProjectState {
  const fail = (message: string): never => {
    throw new ValidationError('project file', [{ path: 'schemaVersion', message }]);
  };
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) fail('project file must be a JSON object');
  let data = raw as Record<string, unknown>;
  let version = data['schemaVersion'];
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) fail('missing or invalid schemaVersion');
  if ((version as number) > currentVersion) {
    fail(`project file is schema v${String(version)}, newer than this app (v${currentVersion}); update the app instead of downgrading the file`);
  }
  while ((version as number) < currentVersion) {
    const step = migrations[version as number];
    if (!step) fail(`no migration from schema v${String(version)}`);
    data = step!({ ...data });
    const next = data['schemaVersion'];
    if (next !== (version as number) + 1) fail(`migration from v${String(version)} produced schemaVersion ${String(next)}`);
    version = next;
  }
  return parseOrThrow(schema, data, 'project file');
}
